import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { NAV } from "@/constants/testIds";

const links = [
  { to: "/", label: "Home", testId: NAV.linkHome },
  { to: "/about", label: "Heritage", testId: NAV.linkAbout },
  { to: "/wholesale", label: "Wholesale", testId: NAV.linkWholesale },
  { to: "/contact", label: "Contact", testId: NAV.linkContact },
];

export default function Nav() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-[background,border-color,padding] duration-500 ease-out ${
        scrolled ? "bg-[color:var(--cream)]/95 backdrop-blur-md border-b border-[color:var(--hairline)] py-3" : "bg-[color:var(--cream)]/70 backdrop-blur-sm lg:bg-transparent lg:backdrop-blur-0 py-3 lg:py-6"
      }`}
    >
      <div className="mx-auto max-w-[1600px] px-5 md:px-10 flex items-center justify-between">
        <Link
          to="/"
          data-testid={NAV.logo}
          className="group flex items-baseline gap-2 min-w-0"
        >
          <span className="font-display text-lg md:text-2xl leading-none tracking-tightest text-[color:var(--ink)] whitespace-nowrap">
            F. Drury <span className="italic text-[color:var(--oxblood)]">&amp;</span> Sons
          </span>
          <span className="hidden xl:inline font-ui text-[10px] uppercase tracking-editorial text-[color:var(--ink-light)] pl-2">
            Est. c.1924 · Wiltshire
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-10">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testId}
              end={l.to === "/"}
              className={({ isActive }) =>
                `font-ui text-[12px] uppercase tracking-editorial link-underline ${
                  isActive ? "text-[color:var(--oxblood)]" : "text-[color:var(--ink)]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-6">
          <a
            href="tel:01793852467"
            className="font-ui text-[12px] uppercase tracking-editorial text-[color:var(--ink)] link-underline"
          >
            01793 852467
          </a>
          <Link to="/contact" className="btn-sharp">
            <span>Enquire</span>
            <svg width="14" height="10" viewBox="0 0 14 10" fill="none" aria-hidden="true">
              <path d="M0 5h13M9 1l4 4-4 4" stroke="currentColor" strokeWidth="1.2" />
            </svg>
          </Link>
        </div>

        <button
          type="button"
          onClick={() => setOpen((s) => !s)}
          data-testid={NAV.mobileToggle}
          aria-label="Toggle menu"
          className="lg:hidden relative w-11 h-11 grid place-items-center border border-[color:var(--ink)] bg-[color:var(--cream)]"
        >
          <span
            className={`block w-5 h-px bg-[color:var(--ink)] transition-transform duration-500 ${
              open ? "translate-y-[3px] rotate-45" : "-translate-y-1"
            }`}
          />
          <span
            className={`block w-5 h-px bg-[color:var(--ink)] transition-transform duration-500 absolute ${
              open ? "-translate-y-[0px] -rotate-45" : "translate-y-1"
            }`}
          />
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid={NAV.mobilePanel}
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.35 }}
            className="lg:hidden bg-[color:var(--cream)] border-t border-[color:var(--hairline)]"
          >
            <div className="px-5 py-8 flex flex-col gap-5">
              {links.map((l, i) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`${l.testId}-mobile`}
                  className={({ isActive }) =>
                    `font-display text-3xl leading-none ${isActive ? "text-[color:var(--oxblood)] italic" : "text-[color:var(--ink)]"}`
                  }
                >
                  <span className="font-ui text-[10px] tracking-editorial align-middle mr-3 text-[color:var(--ink-light)]">
                    0{i + 1}
                  </span>
                  {l.label}
                </NavLink>
              ))}
              <div className="pt-6 mt-2 border-t border-[color:var(--hairline)] flex flex-col gap-2">
                <a href="tel:01793852467" className="font-ui text-sm text-[color:var(--ink)]">01793 852467</a>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
